import React, { useEffect, useState } from 'react';
import { visitsAPI, clientsAPI } from '../../services/api';
import { utcIsoToClientTzInput, clientTzInputToUtcIso } from '../../services/timezone';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';

/** Inline clock-time correction for a visit line inside a draft invoice / pay statement. */
const FixClockTimesDialog = ({ line, clientId, open, onClose, onSaved }) => {
  const [tz, setTz] = useState('America/New_York');
  const [form, setForm] = useState({ check_in_time: '', check_out_time: '', change_reason: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !line) return;
    const cid = line.client_id || clientId;
    (async () => {
      let zone = 'America/New_York';
      try { zone = (await clientsAPI.getById(cid)).data?.timezone || zone; } catch { /* default */ }
      setTz(zone);
      const dateDefault = `${line.date}T${(line.time || '09:00').slice(0, 5)}`;
      setForm({
        check_in_time: line.check_in ? utcIsoToClientTzInput(line.check_in, zone) : dateDefault,
        check_out_time: line.check_out ? utcIsoToClientTzInput(line.check_out, zone) : '',
        change_reason: '',
      });
    })();
  }, [open, line, clientId]);

  const hours = form.check_in_time && form.check_out_time ? (new Date(form.check_out_time) - new Date(form.check_in_time)) / 3600000 : null;

  const save = async () => {
    if (form.check_out_time && !form.check_in_time) return toast.error('Clock-out requires a clock-in time');
    if (hours !== null && hours <= 0) return toast.error('Clock-out must be after clock-in');
    if (!form.change_reason.trim()) return toast.error('Please give a reason for the change');
    setSaving(true);
    try {
      await visitsAPI.updateTimes(line.visit_id, {
        check_in_time: form.check_in_time ? clientTzInputToUtcIso(form.check_in_time, tz) : '',
        check_out_time: form.check_out_time ? clientTzInputToUtcIso(form.check_out_time, tz) : '',
        change_reason: form.change_reason.trim(),
      });
      toast.success('Clock times updated — draft recalculated');
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update clock times'); }
    finally { setSaving(false); }
  };

  if (!line) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-md" data-testid="fix-clock-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Fix clock times</DialogTitle>
          <DialogDescription>{line.date} · {line.caregiver_name || line.client_name} · times in {tz}. Saving both times marks the visit completed and the draft updates automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Clock In</Label><Input type="datetime-local" value={form.check_in_time} onChange={(e) => setForm({ ...form, check_in_time: e.target.value })} className="rounded-xl" data-testid="fix-clock-in-input" /></div>
            <div className="space-y-1"><Label className="text-xs">Clock Out</Label><Input type="datetime-local" value={form.check_out_time} onChange={(e) => setForm({ ...form, check_out_time: e.target.value })} className="rounded-xl" data-testid="fix-clock-out-input" /></div>
          </div>
          {hours !== null && hours > 0 && <p className="text-xs text-muted-foreground" data-testid="fix-clock-hours">Worked: {hours.toFixed(2)} hrs (scheduled {line.hours} hrs)</p>}
          <div className="space-y-1"><Label className="text-xs">Reason for change <span className="text-destructive">*</span></Label><Input value={form.change_reason} onChange={(e) => setForm({ ...form, change_reason: e.target.value })} placeholder="e.g. Caregiver forgot to clock out" className="rounded-xl" data-testid="fix-clock-reason-input" /></div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" className="rounded-xl" onClick={onClose}>Cancel</Button>
            <Button className="rounded-xl" onClick={save} disabled={saving} data-testid="fix-clock-save">{saving ? 'Saving…' : 'Save times'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FixClockTimesDialog;
